import React, { useEffect, useMemo } from 'react';

import { Container, Wrapper } from 'src/components/Layout';
import { ProductPrice } from 'src/types/global-types';
import { Reviews } from 'src/components/Reviews/reviews.types';
import { useGTM } from 'src/features/analytics/hooks/useGtm';
import { KLARNA_PRICE_THRESHOLD, KlarnaPlacement, RefreshKlarnaOSMPlacements } from 'src/components/KlarnaOSM';
import { Nosto } from 'src/components/Nosto/Nosto';
import { ProductMainPrice } from 'src/components/ProductDetail/ProductMainPrice';
import { DeliveryOptions } from 'src/components/ProductDetail/DeliveryOptions';
import ProductActionsConnected from 'src/components/ProductDetail/ProductActionsConnected';
import { ProductAddToBasketModal } from 'src/components/AtbModal/ProductAddToBasketModal';
import { ProductPromotions } from 'src/components/ProductDetail/ProductPromotions';
import { ProductPromotionsOverride } from 'src/components/ProductDetail/ProductPromotionsOverride';
import { useStoreConfig } from 'src/components/AppShell/ConfigProvider';

import { ProductTabs } from './ProductTabs';
import { ProductReviews } from './ProductReviews';
import { DataSheet, Product, Specification } from './utils/product-detail.types';
import { ProductSchema } from './ProductStructuredData';
import { ProductMain } from './ProductMain';
import classes from './ProductDetail.scss';
import { ProductAttrs } from './utils/product-attrs.types';

export type ProductDetailProps = {
    attrs: ProductAttrs;
    benefits: string[];
    configurableOptions?: React.ReactNode;
    dataSheets: DataSheet[];
    parentSku?: string;
    price: ProductPrice;
    product: Product;
    reviews: Reviews | null;
    specifications: Specification[];
    isComingSoon?: boolean;
    testId?: string;
};

export function ProductDetail(props: ProductDetailProps) {
    const { cmsBlocks } = useStoreConfig();

    const isComingSoon = Boolean(props.isComingSoon);
    const isEnabled = props.attrs.delivery || props.attrs.click_collect;

    const promotionsUI = useMemo(() => {
        if (cmsBlocks.productDetailPagePromotionsOverride) {
            return <ProductPromotionsOverride cmsBlock={cmsBlocks.productDetailPagePromotionsOverride} />;
        }

        return (
            props.product.promotions &&
            props.product.promotions.length > 0 && <ProductPromotions promotions={props.product.promotions} />
        );
    }, [cmsBlocks.productDetailPagePromotionsOverride, props.product.promotions]);

    useGTM({
        event: 'view_item',
        ecommerce: {
            currency: 'EUR',
            items: [
                {
                    item_id: props.product.sku,
                    item_name: props.product.name,
                    value: props.price.minimum_price,
                },
            ],
            value: props.price.minimum_price,
        },
    });

    /**
     * Refresh the Klarna placements.
     * Seems to be required when landing on the PDP for the first time
     */
    useEffect(() => RefreshKlarnaOSMPlacements(), []);

    return (
        <>
            <Wrapper
                variant="page-inner-with-breadcrumb"
                className={classes.productMainWrapper}
                data-test-id={props.testId}
            >
                <Container>
                    <ProductMain
                        product={props.product}
                        price={props.price}
                        reviews={props.reviews}
                        attrs={props.attrs}
                    >
                        <ProductActionsConnected
                            isComingSoon={isComingSoon}
                            isComingSoonText={props.product.isComingSoonText}
                            isEnabled={isEnabled}
                            attrs={props.attrs}
                            product={props.product}
                            parentSku={props.parentSku}
                            productPrice={
                                props.price && <ProductMainPrice price={props.price} productType={props.product.type} />
                            }
                            deliveryOptions={
                                <DeliveryOptions
                                    isComingSoon={isComingSoon}
                                    product={props.product}
                                    attrs={props.attrs}
                                />
                            }
                            promotions={promotionsUI}
                            configurableOptions={props.configurableOptions}
                        />
                        <ProductAddToBasketModal />
                        <div className={classes.productKlarnaPlacement}>
                            {/* Display Klarna OSM when above the client's threshold */}
                            {props.price && props.price.minimum_price > KLARNA_PRICE_THRESHOLD && (
                                <KlarnaPlacement
                                    klarnaKey="credit-promotion-badge"
                                    klarnaPrice={props.price}
                                    classNames={['klarnaATMessaging']}
                                />
                            )}
                            {/* Display Klarna OSM when below the client's threshold */}
                            {props.price && props.price.minimum_price < KLARNA_PRICE_THRESHOLD && (
                                <KlarnaPlacement klarnaKey="credit-promotion-badge" klarnaPrice={props.price} />
                            )}
                        </div>
                    </ProductMain>
                </Container>
            </Wrapper>
            <ProductSchema product={props.product} price={props.price} />
            <Wrapper bg="grey-light" variant="page-inner-padded">
                <Container>
                    <ProductTabs
                        description={props.product.description}
                        specifications={props.specifications}
                        dataSheets={props.dataSheets}
                        benefits={props.benefits}
                    />
                </Container>
            </Wrapper>
            <Container className={classes.productNosto}>
                <Nosto type="product" blogPostIds={props?.product?.blog_ids} />
            </Container>
            <Wrapper variant="page-inner">
                <Container>
                    <p className="h2">Rating and Reviews</p>
                    <ProductReviews product={props.product} price={props.price} />
                </Container>
            </Wrapper>
        </>
    );
}
